import type { TrainingModelDef, TrainingType, AdvancedFieldDef } from './types'

// ─── Validation Thresholds ──────────────────────────────────────

export const MIN_IMAGE_DIMENSION = 512
export const MAX_FILE_SIZE_MB = 50
export const MIN_IMAGES = 4
export const MAX_IMAGES = 50
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm']
export const MARKUP_MULTIPLIER = 2
export const MAX_CONCURRENT_JOBS = 3

export const MODEL_TYPE_DESCRIPTIONS = {
  style: 'Collecting information about colours, composition, style.',
  object: 'Learning the shape, texture, and details of your object.',
  character: 'Capturing identity, expressions, and poses of your character.',
} as const

// ─── Shared Advanced Fields ─────────────────────────────────────

const FIELD_OUTPUT_FORMAT: AdvancedFieldDef = {
  key: 'output_lora_format',
  label: 'Output format',
  type: 'select',
  defaultValue: 'fal',
  options: [
    { value: 'fal', label: 'fal (recommended)' },
    { value: 'comfy', label: 'ComfyUI' },
  ],
  description: 'Weight naming scheme for the LoRA output',
}

const FIELD_DEFAULT_CAPTION: AdvancedFieldDef = {
  key: 'default_caption',
  label: 'Default caption',
  type: 'text',
  defaultValue: '',
  description: 'Fallback caption for images without .txt files',
}

const FIELD_IS_STYLE: AdvancedFieldDef = {
  key: 'is_style',
  label: 'Style mode',
  type: 'toggle',
  defaultValue: false,
  description: 'Train a style LoRA instead of subject. Disables auto-captioning and segmentation.',
}

const FIELD_CREATE_MASKS: AdvancedFieldDef = {
  key: 'create_masks',
  label: 'Create masks',
  type: 'toggle',
  defaultValue: true,
  description: 'Auto-generate segmentation masks. Uses face masks for people.',
}

const FIELD_TRAINING_TYPE: AdvancedFieldDef = {
  key: 'training_type',
  label: 'Training focus',
  type: 'select',
  defaultValue: 'balanced',
  options: [
    { value: 'content', label: 'Content' },
    { value: 'style', label: 'Style' },
    { value: 'balanced', label: 'Balanced' },
  ],
  description: 'Balance between learning content vs artistic style',
}

const FIELD_DO_CAPTION: AdvancedFieldDef = {
  key: 'do_caption',
  label: 'Auto-caption',
  type: 'toggle',
  defaultValue: true,
  description: 'Automatically generate descriptions for images',
}

// Video-specific fields
const VIDEO_FIELDS: AdvancedFieldDef[] = [
  {
    key: 'resolution',
    label: 'Resolution',
    type: 'select',
    defaultValue: 'medium',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
    ],
    description: 'Higher resolutions require more memory',
  },
  {
    key: 'aspect_ratio',
    label: 'Aspect ratio',
    type: 'select',
    defaultValue: '1:1',
    options: [
      { value: '16:9', label: '16:9' },
      { value: '1:1', label: '1:1' },
      { value: '9:16', label: '9:16' },
    ],
    description: 'Output aspect ratio',
  },
  {
    key: 'auto_scale_input',
    label: 'Auto-scale input',
    type: 'toggle',
    defaultValue: false,
    description: 'Auto-scale video to target frames and fps',
  },
  {
    key: 'split_input_into_scenes',
    label: 'Split into scenes',
    type: 'toggle',
    defaultValue: true,
    description: 'Split long clips into scenes at boundaries',
  },
]

const LTX_EXTRA_FIELDS: AdvancedFieldDef[] = [
  {
    key: 'rank',
    label: 'LoRA rank',
    type: 'select',
    defaultValue: '32',
    options: [
      { value: '8', label: '8' },
      { value: '16', label: '16' },
      { value: '32', label: '32' },
      { value: '64', label: '64' },
      { value: '128', label: '128' },
    ],
    description: 'Higher values = more capacity but more memory',
  },
  {
    key: 'number_of_frames',
    label: 'Number of frames',
    type: 'number',
    defaultValue: 89,
    min: 9,
    max: 121,
    step: 8,
    description: 'Must satisfy frames % 8 == 1',
  },
  {
    key: 'frame_rate',
    label: 'Frame rate',
    type: 'number',
    defaultValue: 25,
    min: 8,
    max: 60,
    step: 1,
    description: 'Frames per second',
  },
  {
    key: 'with_audio',
    label: 'Enable audio training',
    type: 'toggle',
    defaultValue: false,
    description: 'Joint audio-video training',
  },
]

// ─── 38-Model Catalog (LoRA trainers only) ──────────────────────

function m(
  id: string,
  falEndpoint: string,
  name: string,
  family: TrainingModelDef['family'],
  category: TrainingModelDef['category'],
  pricePerUnit: number,
  pricingUnit: TrainingModelDef['pricingUnit'],
  tags: TrainingModelDef['tags'],
  description: string,
  opts?: Partial<Pick<TrainingModelDef, 'defaultSteps' | 'minSteps' | 'maxSteps' | 'defaultLearningRate' | 'advancedFields' | 'acceptsVideo'>>,
): TrainingModelDef {
  return {
    id,
    falEndpoint,
    name,
    family,
    category,
    pricePerUnit,
    pricingUnit,
    tags,
    description,
    defaultSteps: opts?.defaultSteps ?? 1000,
    minSteps: opts?.minSteps ?? 100,
    maxSteps: opts?.maxSteps ?? 10000,
    defaultLearningRate: opts?.defaultLearningRate ?? 0.0001,
    advancedFields: opts?.advancedFields ?? [],
    acceptsVideo: opts?.acceptsVideo ?? false,
  }
}

export const MODEL_CATALOG: TrainingModelDef[] = [
  // ── Flux Family (14) ──────────────────────────────────────────
  m('flux-lora', 'fal-ai/flux-lora-fast-training', 'Train Flux LoRA', 'flux', 'image', 0.02, 'step', ['popular'],
    'Fast, high quality LoRA training for Flux',
    { defaultLearningRate: 0.00005, advancedFields: [FIELD_IS_STYLE, FIELD_CREATE_MASKS] }),
  m('flux-kontext', 'fal-ai/flux-kontext-trainer', 'Flux Kontext Trainer', 'flux', 'image', 0.0025, 'step', [],
    'Edit-pair training for Flux Kontext',
    { advancedFields: [FIELD_OUTPUT_FORMAT, FIELD_DEFAULT_CAPTION] }),
  m('flux-krea', 'fal-ai/flux-krea-trainer', 'Train Flux Krea LoRA', 'flux', 'image', 0.02, 'step', [],
    'Krea-optimized Flux LoRA training'),
  m('flux-portrait', 'fal-ai/flux-lora-portrait-trainer', 'Train Flux Portraits', 'flux', 'image', 0.024, 'step', [],
    'Portrait-optimized Flux LoRA training'),
  m('flux-turbo', 'fal-ai/turbo-flux-trainer', 'Turbo Flux Trainer', 'flux', 'image', 0.0024, 'step', ['budget'],
    'Fast and cheap Flux training'),
  m('flux-2', 'fal-ai/flux-2-trainer', 'Flux 2 Trainer', 'flux', 'image', 0.008, 'unit', [],
    'Flux 2 LoRA training',
    { defaultLearningRate: 0.00005, advancedFields: [FIELD_OUTPUT_FORMAT, FIELD_DEFAULT_CAPTION] }),
  m('flux-2-edit', 'fal-ai/flux-2-trainer/edit', 'Flux 2 Trainer (Edit)', 'flux', 'image', 0.009, 'unit', [],
    'Edit existing Flux 2 LoRAs'),
  m('flux-2-v2', 'fal-ai/flux-2-trainer-v2', 'Flux 2 Trainer V2', 'flux', 'image', 0.0255, 'unit', [],
    'Latest Flux 2 trainer',
    { advancedFields: [FIELD_OUTPUT_FORMAT, FIELD_DEFAULT_CAPTION] }),
  m('flux-2-v2-edit', 'fal-ai/flux-2-trainer-v2/edit', 'Flux 2 Trainer V2 (Edit)', 'flux', 'image', 0.0226, 'unit', [],
    'Edit existing Flux 2 V2 LoRAs'),
  m('flux-2-klein-4b', 'fal-ai/flux-2-klein-4b-base-trainer', 'Flux 2 Klein 4B', 'flux', 'image', 0.005, 'unit', [],
    'Lightweight 4B parameter Flux trainer'),
  m('flux-2-klein-4b-edit', 'fal-ai/flux-2-klein-4b-base-trainer/edit', 'Flux 2 Klein 4B (Edit)', 'flux', 'image', 0.0046, 'unit', [],
    'Edit existing Klein 4B LoRAs'),
  m('flux-2-klein-9b', 'fal-ai/flux-2-klein-9b-base-trainer', 'Flux 2 Klein 9B', 'flux', 'image', 0.0086, 'unit', [],
    'Mid-size 9B parameter Flux trainer'),
  m('flux-2-klein-9b-edit', 'fal-ai/flux-2-klein-9b-base-trainer/edit', 'Flux 2 Klein 9B (Edit)', 'flux', 'image', 0.008, 'unit', [],
    'Edit existing Klein 9B LoRAs'),

  // ── Qwen Family (11) ──────────────────────────────────────────
  m('qwen-image', 'fal-ai/qwen-image-trainer', 'Qwen Image Trainer', 'qwen', 'image', 0.002, 'step', ['budget'],
    'Budget Qwen image LoRA training',
    { defaultLearningRate: 0.0005 }),
  m('qwen-image-v2', 'fal-ai/qwen-image-trainer-v2', 'Qwen Image Trainer V2', 'qwen', 'image', 0.002, 'step', [],
    'Updated Qwen image trainer'),
  m('qwen-image-2512', 'fal-ai/qwen-image-2512-trainer', 'Qwen Image 2512', 'qwen', 'image', 0.0015, 'step', [],
    'High-res 2512px Qwen trainer'),
  m('qwen-image-2512-v2', 'fal-ai/qwen-image-2512-trainer-v2', 'Qwen Image 2512 V2', 'qwen', 'image', 0.00095, 'step', ['budget'],
    'Cheapest Qwen trainer'),
  m('qwen-image-edit', 'fal-ai/qwen-image-edit-trainer', 'Qwen Image Edit', 'qwen', 'image', 0.004, 'step', [],
    'Qwen image editing LoRA trainer'),
  m('qwen-image-edit-2509', 'fal-ai/qwen-image-edit-2509-trainer', 'Qwen Image Edit 2509', 'qwen', 'image', 0.004, 'step', [],
    'Qwen edit trainer (2509 version)'),
  m('qwen-image-edit-2511', 'fal-ai/qwen-image-edit-2511-trainer', 'Qwen Image Edit 2511', 'qwen', 'image', 0.004, 'step', [],
    'Qwen edit trainer (2511 version)'),
  m('qwen-image-edit-plus', 'fal-ai/qwen-image-edit-plus-trainer', 'Qwen Image Edit Plus', 'qwen', 'image', 0.004, 'step', [],
    'Enhanced Qwen edit trainer'),
  m('qwen-image-layered', 'fal-ai/qwen-image-layered-trainer', 'Qwen Image Layered', 'qwen', 'image', 0.00425, 'unit', [],
    'Layered Qwen image trainer'),

  // ── Z-Image Family (3) ────────────────────────────────────────
  m('z-image', 'fal-ai/z-image-trainer', 'Z Image Trainer', 'z-image', 'image', 0.00226, 'step', [],
    'Z-Image LoRA with content/style balance',
    { advancedFields: [FIELD_TRAINING_TYPE, FIELD_DEFAULT_CAPTION] }),
  m('z-image-base', 'fal-ai/z-image-base-trainer', 'Z-Image Base Trainer', 'z-image', 'image', 0.00085, 'step', ['budget'],
    'Cheapest Z-Image base trainer'),
  m('z-image-turbo-v2', 'fal-ai/z-image-turbo-trainer-v2', 'Z Image Turbo V2', 'z-image', 'image', 0.00085, 'step', ['budget'],
    'Fast turbo Z-Image trainer'),

  // ── Wan Image (1) ─────────────────────────────────────────────
  m('wan-22-image', 'fal-ai/wan-22-image-trainer', 'Wan 2.2 14B Image', 'wan', 'image', 0.0045, 'step', [],
    'Wan 2.2 14B image LoRA training'),

  // ── Wan Video (7) ─────────────────────────────────────────────
  m('wan-21-t2v', 'fal-ai/wan-trainer/t2v', 'Wan-2.1 T2V', 'wan', 'video', 0.005, 'step', [],
    'Wan 2.1 text-to-video LoRA',
    { defaultSteps: 400, defaultLearningRate: 0.0002, advancedFields: VIDEO_FIELDS, acceptsVideo: true }),
  m('wan-21-t2v-14b', 'fal-ai/wan-trainer/t2v-14b', 'Wan-2.1 T2V 14B', 'wan', 'video', 0.005, 'step', [],
    'Wan 2.1 text-to-video 14B',
    { defaultSteps: 400, defaultLearningRate: 0.0002, advancedFields: VIDEO_FIELDS, acceptsVideo: true }),
  m('wan-21-i2v', 'fal-ai/wan-trainer/i2v-720p', 'Wan-2.1 I2V 720p', 'wan', 'video', 0.005, 'step', [],
    'Wan 2.1 image-to-video 720p',
    { defaultSteps: 400, defaultLearningRate: 0.0002, advancedFields: VIDEO_FIELDS, acceptsVideo: true }),
  m('wan-21-flf2v', 'fal-ai/wan-trainer/flf2v-720p', 'Wan-2.1 FLF2V 720p', 'wan', 'video', 0.005, 'step', [],
    'Wan 2.1 first-last-frame to video',
    { defaultSteps: 400, defaultLearningRate: 0.0002, advancedFields: VIDEO_FIELDS, acceptsVideo: true }),
  m('wan-22-t2v', 'fal-ai/wan-22-trainer/t2v-a14b', 'Wan-2.2 T2V A14B', 'wan', 'video', 0.004, 'step', ['budget'],
    'Wan 2.2 text-to-video',
    { defaultSteps: 400, defaultLearningRate: 0.0002, advancedFields: VIDEO_FIELDS, acceptsVideo: true }),
  m('wan-22-i2v', 'fal-ai/wan-22-trainer/i2v-a14b', 'Wan-2.2 I2V A14B', 'wan', 'video', 0.005, 'step', [],
    'Wan 2.2 image-to-video',
    { defaultSteps: 400, defaultLearningRate: 0.0002, advancedFields: VIDEO_FIELDS, acceptsVideo: true }),

  // ── LTX (3) ───────────────────────────────────────────────────
  m('ltx-video', 'fal-ai/ltx-video-trainer', 'LTX Video Trainer', 'ltx', 'video', 0.002, 'step', ['budget'],
    'LTX video LoRA trainer',
    { defaultSteps: 2000, maxSteps: 20000, defaultLearningRate: 0.0002, advancedFields: [...VIDEO_FIELDS, ...LTX_EXTRA_FIELDS], acceptsVideo: true }),
  m('ltx2-video', 'fal-ai/ltx2-video-trainer', 'LTX-2 Video Trainer', 'ltx', 'video', 0.0048, 'step', [],
    'LTX-2 video LoRA with audio support',
    { defaultSteps: 2000, maxSteps: 20000, defaultLearningRate: 0.0002, advancedFields: [...VIDEO_FIELDS, ...LTX_EXTRA_FIELDS], acceptsVideo: true }),
  m('ltx2-v2v', 'fal-ai/ltx2-v2v-trainer', 'LTX-2 V2V Trainer', 'ltx', 'video', 0, 'step', [],
    'LTX-2 video-to-video trainer',
    { defaultSteps: 2000, maxSteps: 20000, advancedFields: VIDEO_FIELDS, acceptsVideo: true }),

  // ── Hunyuan (1) ───────────────────────────────────────────────
  m('hunyuan', 'fal-ai/hunyuan-video-lora-training', 'Train Hunyuan LoRA', 'hunyuan', 'video', 0.005, 'step', [],
    'Hunyuan video LoRA trainer',
    { minSteps: 1, maxSteps: 5000, advancedFields: [FIELD_DO_CAPTION, ...VIDEO_FIELDS], acceptsVideo: true }),

]

// ─── Helpers ────────────────────────────────────────────────────

export const MODEL_FAMILIES: { family: TrainingModelDef['family']; label: string; category: TrainingModelDef['category'] }[] = [
  { family: 'flux', label: 'Flux', category: 'image' },
  { family: 'qwen', label: 'Qwen', category: 'image' },
  { family: 'z-image', label: 'Z-Image', category: 'image' },
  { family: 'wan', label: 'Wan', category: 'image' }, // wan-22-image is image, wan-video are video
  { family: 'ltx', label: 'LTX', category: 'video' },
  { family: 'hunyuan', label: 'Hunyuan', category: 'video' },
]

export function getModelById(id: string): TrainingModelDef | undefined {
  return MODEL_CATALOG.find((m) => m.id === id)
}

export function getDefaultTrainerForType(type: TrainingType): string {
  // Recommend best default trainer based on training intent
  switch (type) {
    case 'character':
      return 'flux-portrait'
    case 'object':
      return 'flux-lora'
    case 'style':
      return 'z-image'
    default:
      return 'flux-lora'
  }
}

export function getModelsByFamily(family: TrainingModelDef['family']): TrainingModelDef[] {
  return MODEL_CATALOG.filter((m) => m.family === family)
}

/** Image-only models (excludes video trainers) for the image training dialog */
export function getImageModels(): TrainingModelDef[] {
  return MODEL_CATALOG.filter((m) => m.category === 'image')
}

/** Video-only models for the video training dialog */
export function getVideoModels(): TrainingModelDef[] {
  return MODEL_CATALOG.filter((m) => m.category === 'video')
}

/** Model families that have at least one image model */
export const IMAGE_MODEL_FAMILIES = MODEL_FAMILIES.filter(
  (f) => f.category === 'image',
)

/** Upload guidance text per model type */
export const UPLOAD_GUIDANCE: Record<TrainingType, { title: string; hint: string }> = {
  style: { title: 'Upload style references', hint: '10-20 images in the style you want to replicate' },
  object: { title: 'Upload object photos', hint: '10-20 photos of your object from different angles' },
  character: { title: 'Upload character photos', hint: '10-20 photos with varied poses, angles, and lighting' },
}
